import { Platform, View } from 'react-native';
import React, { useCallback } from 'react';
import {
  Banner,
  Footer,
  Input,
  KeyboardScreenContainer,
  Select,
  Text,
} from '~/components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import type { MarisaAutoStepsProps } from '../..';
import type { Gender } from '~/utils/form';
import { isBirthDateValid, isFullName } from '~/utils/yup';
import { useSalesFunnel } from '~/stores';

const schema = yup
  .object()
  .shape({
    fullName: isFullName(),
    birthDate: isBirthDateValid({}),
    maritalStatus: yup.string().required('Este campo é obrigatório'),
    gender: yup.string().required('Este campo é obrigatório'),
  })
  .required();

interface PersonalInformationForm {
  fullName: string;
  birthDate: string;
  maritalStatus: string;
  gender: Gender;
}

const PersonalInformation = ({ onContinue }: MarisaAutoStepsProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PersonalInformationForm>({
    resolver: yupResolver(schema),
  });

  const { setPersonalInformation } = useMarisaAutoForm();
  const { setInfos } = useSalesFunnel();

  const onSubmit = useCallback(
    (data: PersonalInformationForm) => {
      setPersonalInformation(data);
      setInfos({
        fullName: data.fullName,
        birthDate: data.birthDate,
        maritalStatus: data.maritalStatus,
        gender: data.gender,
      });
      onContinue();
    },
    [onContinue, setInfos, setPersonalInformation]
  );

  return (
    <KeyboardScreenContainer
      footer={
        <View
          className={`flex xl:hidden ${
            Platform.OS === 'web' ? 'py-4 px-6' : ''
          }`}
        >
          <Footer onSubmit={handleSubmit(onSubmit)} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner type="marisa-auto" />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24">
          <Text className="text-xl mb-4 text-gray-600">
            Informações pessoais
          </Text>

          <Text className="text-gray-400 text-sm mb-8">
            Prometo ser o mais breve, já estamos separando o melhor para você,
            mas antes precisamos saber como entrar em contato com você.
          </Text>

          <Input
            name="fullName"
            label="Nome completo"
            placeholder="Nome e Sobrenome"
            control={control}
            error={errors.fullName?.message}
          />

          <Input
            name="birthDate"
            label="Data de nascimento"
            placeholder="XX/XX/XXXX"
            maskConfig={{ type: 'custom', mask: '99/99/9999' }}
            control={control}
            error={errors.birthDate?.message}
            maxLength={10}
            keyboardType="decimal-pad"
          />

          <Select
            name="maritalStatus"
            label="Estado civil"
            placeholder="Selecione o seu estado civil"
            items={[
              { label: 'SOLTEIRO(A)', value: 'single' },
              { label: 'CASADO(A)', value: 'married' },
              { label: 'DIVORCIADO(A)', value: 'divorced' },
              { label: 'VIÚVO(A)', value: 'widower' },
            ]}
            control={control}
            error={errors.maritalStatus?.message}
          />

          <Select
            name="gender"
            label="Gênero"
            placeholder="Selecione o seu gênero"
            items={[
              { label: 'FEMININO', value: 'female' },
              { label: 'MASCULINO', value: 'male' },
            ]}
            control={control}
            error={errors.gender?.message}
          />

          <Footer className="hidden" onSubmit={handleSubmit(onSubmit)} />
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default PersonalInformation;
